class CollectionTriggerGiftManager {
  constructor(config = {}) {
    this.collectionHandle = config.collectionHandle;
    this.giftProductHandle = config.giftProductHandle;
    this.giftVariantId = null;
    this.collectionProductIds = null;

    this.sessionKey = 'ctgm_userRemoved_' + this.collectionHandle;

    this.isProcessing = false;
    this.internalOperation = false;
    this.debounceTimer = null;
    this.initialized = false;
    this.lastGiftInCart = false;

    this.cartCache = null;
    this.cacheExpiry = 0;
    this.cacheDuration = 2000;

    // Intercept fetch immediately so cart mutations are caught even during boot
    this._interceptFetch();
    this._boot();
  }

  // Wrap window.fetch so every /cart/add, /cart/update, etc. triggers a check
  _interceptFetch() {
    const self = this;
    const orig = window.fetch;
    window.fetch = async function (...args) {
      const res = await orig(...args);
      const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url ?? '');
      const isCartMutation =
        url.includes('/cart/add') ||
        url.includes('/cart/update') ||
        url.includes('/cart/change') ||
        url.includes('/cart/clear');
      if (isCartMutation && !self.internalOperation) {
        self._invalidate();
        self._schedule(1200);
      }
      return res;
    };
  }

  async _boot() {
    await Promise.all([
      this._resolveGiftVariant(),
      this._loadCollectionProducts(),
    ]);
    this._setupListeners();
    // Catch cases where the page loads with collection items already in cart
    setTimeout(() => this._check(), 1500);
  }

  async _resolveGiftVariant() {
    try {
      const res = await fetch(`/products/${this.giftProductHandle}.js`);
      if (!res.ok) throw new Error(res.status);
      const product = await res.json();
      this.giftVariantId = product.variants[0]?.id?.toString() ?? null;
    } catch (err) {
      console.error('[CTGM] Could not resolve gift variant:', err);
    }
  }

  async _loadCollectionProducts() {
    try {
      const res = await fetch(
        `/collections/${this.collectionHandle}/products.json?limit=250`
      );
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      this.collectionProductIds = new Set(
        data.products.map((p) => p.id.toString())
      );
    } catch (err) {
      console.error('[CTGM] Could not load collection products:', err);
      this.collectionProductIds = new Set();
    }
  }

  // Event listeners as a backup for themes that do fire cart events
  _setupListeners() {
    ['cart:updated', 'cart:changed', 'cart:item-added'].forEach((name) => {
      document.addEventListener(name, (e) => {
        if (!this.internalOperation && e.detail?.source !== 'ctgm') {
          this._invalidate();
          this._schedule(800);
        }
      });
    });

    ['quantity-update:add', 'quantity-update:subtract', 'quantity-update:remove'].forEach((name) => {
      document.addEventListener(name, () => {
        if (!this.internalOperation) {
          this._invalidate();
          this._schedule(600);
        }
      });
    });

    document.addEventListener('click', (e) => {
      if (
        !this.internalOperation &&
        e.target.matches('[data-quantity="0"], .remove, [data-remove], .cart__remove')
      ) {
        this._invalidate();
        this._schedule(900);
      }
    });
  }

  _schedule(delay = 1000) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this._check(), delay);
  }

  _invalidate() {
    this.cartCache = null;
    this.cacheExpiry = 0;
  }

  async _getCart() {
    const now = Date.now();
    if (this.cartCache && now < this.cacheExpiry) return this.cartCache;
    try {
      const res = await fetch('/cart.js', {
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!res.ok) throw new Error(res.status);
      this.cartCache = await res.json();
      this.cacheExpiry = now + this.cacheDuration;
      return this.cartCache;
    } catch (err) {
      console.error('[CTGM] Cart fetch error:', err);
      return this.cartCache;
    }
  }

  _hasCollectionItem(cart) {
    if (!this.collectionProductIds?.size || !cart?.items) return false;
    return cart.items.some(
      (item) =>
        this.collectionProductIds.has(item.product_id.toString()) &&
        item.variant_id.toString() !== this.giftVariantId
    );
  }

  _getGiftItem(cart) {
    if (!this.giftVariantId || !cart?.items) return null;
    return (
      cart.items.find(
        (item) =>
          item.variant_id.toString() === this.giftVariantId &&
          item.properties?._ctgm_gift === 'true'
      ) ?? null
    );
  }

  _isUserRemoved() {
    try {
      return sessionStorage.getItem(this.sessionKey) === 'true';
    } catch {
      return false;
    }
  }

  _setUserRemoved(val) {
    try {
      val
        ? sessionStorage.setItem(this.sessionKey, 'true')
        : sessionStorage.removeItem(this.sessionKey);
    } catch {}
  }

  async _check() {
    // Skip if boot data isn't ready yet
    if (!this.collectionProductIds || !this.giftVariantId) return;
    if (this.isProcessing) return;

    this.isProcessing = true;

    try {
      const cart = await this._getCart();
      if (!cart) return;

      const hasCollectionItem = this._hasCollectionItem(cart);
      const giftItem = this._getGiftItem(cart);
      const giftIsInCart = !!giftItem;

      // Detect manual removal: gift was here last check, now gone, not our doing
      if (this.initialized && this.lastGiftInCart && !giftIsInCart && hasCollectionItem) {
        this._setUserRemoved(true);
      }

      this.lastGiftInCart = giftIsInCart;
      this.initialized = true;

      // Only trigger when a collection item is in the cart.
      // Once the gift is added it stays — even if the collection item is later removed.
      if (hasCollectionItem && !giftIsInCart && !this._isUserRemoved()) {
        await this._addGift();
      }
    } catch (err) {
      console.error('[CTGM] Check error:', err);
    } finally {
      this.isProcessing = false;
    }
  }

  async _addGift() {
    try {
      this.internalOperation = true;

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: parseInt(this.giftVariantId),
              quantity: 1,
              properties: { _ctgm_gift: 'true' },
            },
          ],
        }),
      });

      if (!res.ok) {
        console.error('[CTGM] Add gift failed:', await res.text());
        return;
      }

      this._invalidate();
      this.lastGiftInCart = true;
      this._toast('🎁 Free gift added to your cart!');
      setTimeout(() => this._refreshCartUI(), 400);
    } catch (err) {
      console.error('[CTGM] Add gift error:', err);
    } finally {
      setTimeout(() => {
        this.internalOperation = false;
      }, 1500);
    }
  }

  async _removeGift(itemKey) {
    try {
      this.internalOperation = true;

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [itemKey]: 0 } }),
      });

      this._invalidate();
      this.lastGiftInCart = false;
      setTimeout(() => this._refreshCartUI(), 400);
    } catch (err) {
      console.error('[CTGM] Remove gift error:', err);
    } finally {
      setTimeout(() => {
        this.internalOperation = false;
      }, 1500);
    }
  }

  _refreshCartUI() {
    // Update cart count badge
    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        document.querySelectorAll('[data-js-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
        });
      })
      .catch(() => {});

    // Standard events (caught by themes that listen)
    ['cart:updated', 'cart:changed', 'cart:refresh'].forEach((name) => {
      const evt = new CustomEvent(name, {
        detail: { source: 'ctgm', timestamp: Date.now() },
      });
      document.dispatchEvent(evt);
      window.dispatchEvent(evt);
    });

    // Stiletto direct refresh methods
    if (typeof theme !== 'undefined') {
      theme.quickCart?.refresh?.();
      theme.cart?.refresh?.();
    }

    // Re-render the quick cart drawer HTML
    fetch('/cart?view=drawer')
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newItems = doc.querySelector('.cart__items, .quick-cart__items');
        document.querySelectorAll('.quick-cart__items, .cart-drawer__items').forEach((el) => {
          if (newItems) el.innerHTML = newItems.innerHTML;
        });
      })
      .catch(() => {});

    // Re-render the /cart page items section
    if (window.location.pathname.includes('/cart')) {
      fetch('/cart')
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newItems = doc.querySelector('.cart__form-items, .cart__items, .cart-items');
          const curItems = document.querySelector('.cart__form-items, .cart__items, .cart-items');
          if (newItems && curItems) curItems.innerHTML = newItems.innerHTML;
        })
        .catch(() => {});
    }
  }

  _toast(message, color = '#28a745') {
    const el = document.createElement('div');
    el.style.cssText = `
      position:fixed;top:20px;right:20px;background:${color};color:#fff;
      padding:12px 20px;border-radius:6px;box-shadow:0 4px 12px rgba(0,0,0,.3);
      z-index:10000;font-family:-apple-system,BlinkMacSystemFont,sans-serif;
      font-size:14px;font-weight:500;transform:translateX(400px);transition:transform .3s ease;
    `;
    el.textContent = message;
    document.body.appendChild(el);
    setTimeout(() => {
      el.style.transform = 'translateX(0)';
    }, 100);
    setTimeout(() => {
      el.style.transform = 'translateX(400px)';
      setTimeout(() => el.remove(), 300);
    }, 4000);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.ctgm = new CollectionTriggerGiftManager({
    collectionHandle: 'traditional-refills-no-bundle-100ml',
    giftProductHandle: 'wood-diffuser',
  });
});

window.addEventListener('beforeunload', () => {
  clearTimeout(window.ctgm?.debounceTimer);
});
